import { useEffect, useRef, useState } from 'react';
import { Checkbox, Form, Typography } from 'antd';
import styled from '@emotion/styled';

import { formatDollarsFromCents } from 'utilities/helpers';

const { Title, Text } = Typography;

const SCROLL_BOTTOM_THRESHOLD_PX = 5;

const DisclosureCheckbox = styled(Checkbox)`
  &.ant-checkbox-wrapper {
    align-items: flex-start;
  }

  .ant-checkbox {
    flex-shrink: 0;
    top: 2px;
  }

  text-align: justify;
  text-justify: inter-word;
`;

const ThingsToKnowList = styled.ol`
  margin: 0;
  padding-inline-start: var(--spacing-lg);

  li::marker {
    color: var(--color-text-secondary);
    font-weight: bold;
  }
`;

const disclosureValidator = (_, value) =>
  value
    ? Promise.resolve()
    : Promise.reject('Customer must acknowledge and accept to continue.');

const PrecoDisclosures = ({ offer, outstandingBalance, expirationDate }) => {
  const resolvedAmount = outstandingBalance - offer.settlementBalance;
  const lastInstallmentDueDate = expirationDate.format('LL');

  const thingsToKnowRef = useRef(null);
  const [hasReadThingsToKnow, setHasReadThingsToKnow] = useState(false);

  useEffect(() => {
    const scrollbox = thingsToKnowRef.current;
    if (scrollbox && scrollbox.scrollHeight <= scrollbox.clientHeight) {
      setHasReadThingsToKnow(true);
    }
  }, []);

  const handleThingsToKnowScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_BOTTOM_THRESHOLD_PX) {
      setHasReadThingsToKnow(true);
    }
  };

  const thingsToKnow = [
    `Your account will close once you enroll, and you will no longer have charging privileges regardless of the outcome of the settlement agreement.`,
    `No new interest or fees while you're enrolled — just your settlement balance.`,
    `Enrolling doesn't stop your account from going further past due or charging off — keep paying at least your minimum payment by each due date to avoid charging off.`,
    `Pay off the settlement by the due date and make your minimum monthly payments, and your account reports as "paid in full for less than full balance." Until then, it reports as paying under a partial payment agreement and may reflect delinquency status up to and including a charged off status if minimum monthly payments are not made.`,
    `You have ${offer.durationMonths} months from the Settlement Offer Date to pay the agreed amount, automatically ending ${lastInstallmentDueDate} if not already completed.`,
    `Payments will be allocated to principal first, then accrued interest and fees.`,
    `A single missed or returned payment doesn't by itself cause failure — only the total paid by the end of the Program Period does. If a payment is returned (for example, for insufficient funds), your balance reverts to what it was before that payment, and that amount will still need to be paid to satisfy the settlement. We'll notify you right away if this happens so you can make it up.`,
    `If total cleared payments are below the Total Settlement Amount by Term end, the full remaining balance becomes due with no penalty interest/fees; credit reporting reflects delinquency status at time of failure.`,
    `Debt considered resolved through a settlement may be taxable — we may send a 1099-C. Talk to a tax professional if you have questions.`,
  ];

  return (
    <div style={{ padding: '0 var(--spacing-lg) var(--spacing-md)' }}>
      <Title level={5} style={{ marginTop: 0 }}>
        Offer Recap
      </Title>
      <Form.Item
        name="offerRecap"
        valuePropName="checked"
        rules={[{ validator: disclosureValidator }]}
        style={{ marginBottom: 0 }}
      >
        <DisclosureCheckbox data-testid="offerRecap-checkbox">
          You qualify for a settlement offer. You'd pay{' '}
          <strong>{formatDollarsFromCents(offer.settlementBalance)}</strong> to
          resolve your account — the remaining amount of{' '}
          <strong>{formatDollarsFromCents(resolvedAmount)}</strong> will be
          considered resolved — with a plan due date of{' '}
          <strong>{lastInstallmentDueDate}</strong>. Enrolling is completely
          voluntary.
        </DisclosureCheckbox>
      </Form.Item>

      <Title level={5} style={{ marginTop: 'var(--spacing-md)' }}>
        Things to Know
      </Title>
      <div
        ref={thingsToKnowRef}
        onScroll={handleThingsToKnowScroll}
        data-testid="thingsToKnow-scrollbox"
        style={{
          maxHeight: 320,
          overflowY: 'auto',
          padding: '0 var(--spacing-md) 0 var(--spacing-sm)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--border-radius)',
          backgroundColor: 'var(--color-fill-quaternary)',
        }}
      >
        <ThingsToKnowList>
          {thingsToKnow.map((item) => (
            <li key={item} style={{ padding: '4px 0' }}>
              <Text
                style={{
                  display: 'block',
                  textAlign: 'justify',
                  textJustify: 'inter-word',
                }}
              >
                {item}
              </Text>
            </li>
          ))}
        </ThingsToKnowList>
      </div>

      <Form.Item
        name="thingsToKnowAck"
        valuePropName="checked"
        rules={[{ validator: disclosureValidator }]}
        style={{ marginTop: 'var(--spacing-md)', marginBottom: 0 }}
      >
        <DisclosureCheckbox
          data-testid="thingsToKnow-checkbox"
          disabled={!hasReadThingsToKnow}
        >
          Customer agrees to enroll under the terms reviewed
        </DisclosureCheckbox>
      </Form.Item>
      {!hasReadThingsToKnow && (
        <Text type="secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
          Scroll through and read all terms above to continue.
        </Text>
      )}
    </div>
  );
};

export default PrecoDisclosures;
